import { trpc } from "@client/trpc";
import { toast } from "sonner";
import { useImpersonationRefValue } from "@client/impersonationRef";
import { videoStateQueryInput } from "@client/utils/videoStateQueryInput";
import { useCallback } from "react";
import { useSetChecklistMark } from "./useSetChecklistMark";
import { useTodoRailSync } from "./TodoRailSyncProvider";
import { type TodoStepManualTarget } from "@lib/todoRail/todoRailModel";

/**
 * Sets a step's state because the funder said so — "Mark as complete" / "Mark
 * as not done".
 *
 * It writes the SAME field the automatic machinery reads, per step kind: a
 * video's completion flag, or an observation row. There is deliberately no
 * override layer sitting over the derived answer — two sources of truth for one
 * question means a step can disagree with itself, and the funder can never hand
 * it back to the app once they've touched it.
 *
 * What "not done" MEANS differs by kind, and both readings are deliberate:
 * - An observed mark is RESET. The detector re-arms, so reading the notes
 *   properly afterwards ticks it again — which is the point of a reset.
 * - A video's completion is CLEARED, and the automatic path only ever latches
 *   it back on by actually watching to the threshold.
 *
 * Neither is an exemption. Nothing here can say "this step never counts"; if
 * that turns out to be wanted, it is a third state, not a stretched meaning of
 * this one.
 */
export function useSetTodoStepDone(
  roundId: string,
): (target: TodoStepManualTarget, done: boolean) => void {
  const viewAsUserId = useImpersonationRefValue();
  const utils = trpc.useUtils();
  const setMark = useSetChecklistMark(roundId);
  const progressInput = videoStateQueryInput(roundId, viewAsUserId);

  // Queued like the other rail writes — see TodoRailSyncProvider. This was the
  // last one on a raw mutation, so a "Mark as complete" made offline showed the
  // tick, dropped the write and reverted on the next refetch.
  const sync = useTodoRailSync();

  return useCallback(
    (target: TodoStepManualTarget, done: boolean) => {
      // Without the outbox nothing is durable, so don't paint an optimistic
      // state the server may never hear about — but SAY so. This return used to
      // be silent, which made the menu item look broken in the one case it is
      // reliably reachable: an admin viewing as a funder, where the rail
      // deliberately refuses to move that funder's progress. A control that
      // quietly does nothing is indistinguishable from a bug.
      //
      // The condition is the ABSENCE of the progress enqueuers rather than a
      // null context: viewing as someone else now keeps the visit outbox alive
      // (History is the viewer's own), so the context is present and only these
      // two are missing. Reading the absence directly means this can't drift
      // from whatever the provider decides to withhold.
      const enqueueVideoCompletion = sync?.enqueueVideoCompletion;
      if (sync?.enqueueMark == null || enqueueVideoCompletion == null) {
        toast.info(
          viewAsUserId != null
            ? "You can't change someone else's progress while viewing as them."
            : "Still connecting — try that again in a moment.",
        );
        return;
      }
      if (target.kind === "mark") {
        setMark(target.itemKey, done);
        return;
      }

      const clientUpdatedAt = new Date().toISOString();

      // Optimistic: the rail reads completion out of this same cache entry, so
      // the tick lands on the row immediately rather than after a round trip.
      utils.getUserVideoState.setData(progressInput, (prev) => {
        if (prev == null) return prev;
        const idx = prev.progress.findIndex(
          (row) => row.videoId === target.videoId,
        );
        const completedAt = done ? clientUpdatedAt : null;
        if (idx === -1) {
          return {
            ...prev,
            progress: [
              ...prev.progress,
              {
                videoId: target.videoId,
                watchedSeconds: 0,
                clientUpdatedAt,
                completedAt,
                completionUpdatedAt: clientUpdatedAt,
              },
            ],
          };
        }
        return {
          ...prev,
          progress: prev.progress.map((row, i) =>
            i === idx
              ? { ...row, completedAt, completionUpdatedAt: clientUpdatedAt }
              : row,
          ),
        };
      });

      enqueueVideoCompletion({
        roundId,
        videoId: target.videoId,
        completed: done,
        clientUpdatedAt,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- progressInput is rebuilt each render; its parts are the real deps.
    [roundId, viewAsUserId, setMark, sync, utils],
  );
}
